// FirebaseUtils.js - AI SDR Firebase utilities
import { auth, db } from "../../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  Timestamp,
} from "firebase/firestore";

/**
 * Utility functions for AI SDR Firebase operations
 */
const FirebaseUtils = {
  /**
   * Get all campaigns that a user has access to (owned or shared)
   *
   * @param {string} uid - User ID
   * @returns {Promise<Array>} - Array of campaigns
   */
  getUserCampaigns: async (uid) => {
    if (!uid) return [];

    try {
      const campaigns = [];

      // 1. Get user's own campaigns
      const ownCampaignsRef = collection(
        db,
        "userContent",
        uid,
        "aiSdr",
        "collections",
        "campaigns"
      );
      const ownCampaignsSnapshot = await getDocs(ownCampaignsRef);

      ownCampaignsSnapshot.forEach((doc) => {
        campaigns.push({
          id: doc.id,
          ...doc.data(),
          isOwner: true,
        });
      });

      // 2. Get campaigns shared directly with the user
      const userEmail = auth.currentUser?.email;
      if (userEmail) {
        const domain = userEmail.split("@")[1];

        // Get access records for this user or their domain
        const accessQuery = query(
          collection(db, "teamAccess"),
          where("resourceType", "==", "campaign"),
          where("accessorUid", "==", uid)
        );

        const accessSnapshot = await getDocs(accessQuery);

        // Get domain-wide access records
        const domainAccessQuery = query(
          collection(db, "teamAccess"),
          where("resourceType", "==", "campaign"),
          where("domain", "==", domain)
        );

        const domainAccessSnapshot = await getDocs(domainAccessQuery);

        // Combine individual and domain access records
        const accessRecords = [];
        accessSnapshot.forEach((doc) => accessRecords.push(doc.data()));
        domainAccessSnapshot.forEach((doc) => accessRecords.push(doc.data()));

        // Fetch each shared campaign
        for (const access of accessRecords) {
          try {
            const path = access.resourcePath.split("/");
            const ownerUid = path[1];
            const campaignId = path[4];

            // Skip campaigns from this user (already fetched)
            if (ownerUid === uid) continue;

            const campaignDoc = await getDoc(
              doc(
                db,
                "userContent",
                ownerUid,
                "aiSdr",
                "collections",
                "campaigns",
                campaignId
              )
            );

            if (campaignDoc.exists()) {
              campaigns.push({
                id: campaignDoc.id,
                ...campaignDoc.data(),
                ownerUid: ownerUid,
                accessLevel: access.accessLevel,
                isOwner: false,
              });
            }
          } catch (error) {
            console.error("Error fetching shared campaign:", error);
          }
        }
      }

      // Sort by creation date
      return campaigns.sort((a, b) => {
        return (b.createdAt?.toDate() || 0) - (a.createdAt?.toDate() || 0);
      });
    } catch (error) {
      console.error("Error getting user campaigns:", error);
      return [];
    }
  },

  /**
   * Share a campaign with users or domains
   *
   * @param {string} campaignId - Campaign ID
   * @param {Object} shareSettings - Settings for sharing
   * @param {Array} shareSettings.users - Array of user emails
   * @param {Array} shareSettings.domains - Array of domains
   * @param {string} shareSettings.permissions - Permission level
   * @returns {Promise<boolean>} - Success state
   */
  shareCampaign: async (campaignId, shareSettings) => {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error("No authenticated user");

      // Update campaign document with share settings
      const campaignRef = doc(
        db,
        "userContent",
        user.uid,
        "aiSdr",
        "campaigns",
        campaignId
      );

      await updateDoc(campaignRef, {
        sharedWith: shareSettings,
      });

      // Create access records for users
      if (shareSettings.users && shareSettings.users.length > 0) {
        for (const userEmail of shareSettings.users) {
          try {
            // Find user by email
            const userQuery = query(
              collection(db, "usersNew"),
              where("email", "==", userEmail)
            );

            const userSnapshot = await getDocs(userQuery);

            if (!userSnapshot.empty) {
              const userData = userSnapshot.docs[0].data();
              const accessorUid = userSnapshot.docs[0].id;

              // Create access record
              await addDoc(collection(db, "teamAccess"), {
                resourceType: "campaign",
                resourcePath: `userContent/${user.uid}/aiSdr/collections/campaigns/${campaignId}`,
                ownerUid: user.uid,
                accessorUid: accessorUid,
                accessLevel: shareSettings.permissions,
                domain: userEmail.split("@")[1] || "",
                createdAt: Timestamp.now(),
              });
            }
          } catch (error) {
            console.error("Error creating user access record:", error);
          }
        }
      }

      // Create access records for domains
      if (shareSettings.domains && shareSettings.domains.length > 0) {
        for (const domain of shareSettings.domains) {
          await addDoc(collection(db, "teamAccess"), {
            resourceType: "campaign",
            resourcePath: `userContent/${user.uid}/aiSdr/collections/campaigns/${campaignId}`,
            ownerUid: user.uid,
            accessorUid: null, // Null for domain-wide access
            accessLevel: shareSettings.permissions,
            domain: domain,
            createdAt: Timestamp.now(),
          });
        }
      }

      return true;
    } catch (error) {
      console.error("Error sharing campaign:", error);
      return false;
    }
  },

  /**
   * Get users from the same domain as the current user
   *
   * @returns {Promise<Array>} - Array of users
   */
  getDomainUsers: async () => {
    try {
      const user = auth.currentUser;
      if (!user || !user.email) return [];

      const domain = user.email.split("@")[1];

      const usersQuery = query(
        collection(db, "usersNew"),
        where("domain", "==", domain)
      );

      const usersSnapshot = await getDocs(usersQuery);

      const users = [];
      usersSnapshot.forEach((doc) => {
        if (doc.id !== user.uid) {
          // Exclude current user
          users.push({
            uid: doc.id,
            ...doc.data(),
          });
        }
      });

      return users;
    } catch (error) {
      console.error("Error getting domain users:", error);
      return [];
    }
  },

  /**
   * Check if user has access to a specific campaign
   *
   * @param {string} ownerUid - Owner's user ID
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<Object>} - Access details or null
   */
  checkCampaignAccess: async (ownerUid, campaignId) => {
    try {
      const user = auth.currentUser;
      if (!user) return null;

      // If user is the owner, they have full access
      if (user.uid === ownerUid) {
        return { accessLevel: "owner" };
      }

      // Check for direct user access
      const userAccessQuery = query(
        collection(db, "teamAccess"),
        where(
          "resourcePath",
          "==",
          `userContent/${ownerUid}/aiSdr/collections/campaigns/${campaignId}`
        ),
        where("accessorUid", "==", user.uid)
      );

      const userAccessSnapshot = await getDocs(userAccessQuery);

      if (!userAccessSnapshot.empty) {
        return {
          accessLevel: userAccessSnapshot.docs[0].data().accessLevel,
          source: "direct",
        };
      }

      // Check for domain access
      if (user.email) {
        const domain = user.email.split("@")[1];

        const domainAccessQuery = query(
          collection(db, "teamAccess"),
          where(
            "resourcePath",
            "==",
            `userContent/${ownerUid}/aiSdr/collections/campaigns/${campaignId}`
          ),
          where("domain", "==", domain)
        );

        const domainAccessSnapshot = await getDocs(domainAccessQuery);

        if (!domainAccessSnapshot.empty) {
          return {
            accessLevel: domainAccessSnapshot.docs[0].data().accessLevel,
            source: "domain",
          };
        }
      }

      // No access
      return null;
    } catch (error) {
      console.error("Error checking campaign access:", error);
      return null;
    }
  },
};

export default FirebaseUtils;
